// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Chip, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { ReviewChecklistItem } from './build-review-checklist';
import styles from './review-summary.module.css';

export type ReviewChecklistRowProps = {
  item: ReviewChecklistItem;
  isLastRow: boolean;
  onReview: () => void;
};

export const ReviewChecklistRow = defineComponent({
  displayName: 'ReviewChecklistRow',
  useSetup: ({ item, isLastRow, onReview }: ReviewChecklistRowProps) => ({
    item,
    isLastRow,
    onReview,
  }),
  View: ({ item, isLastRow, onReview }) => (
    <li
      className={classNames(
        styles['checklist-row'],
        isLastRow && styles['checklist-row-last'],
      )}
      data-testid={`bank-upload-review-checklist-${item.id}`}
    >
      <div className={styles['checklist-row-surface']}>
        <div className={styles['icon-wrapper']}>
          <AppIcon icon={item.icon} color="inherit" aria-hidden />
        </div>

        <div className={styles['row-text']}>
          <Typography component="p" variant="body1" className={styles['row-title']}>
            {item.sectionLabel}
          </Typography>
          <Typography component="p" variant="body2" className={styles['row-count']}>
            {item.countLabel}
          </Typography>
        </div>

        <Chip
          size="small"
          color={item.status === 'complete' ? 'success' : 'warning'}
          className={styles['status-chip']}
          label={
            <Translation
              i18nKey={
                item.status === 'complete'
                  ? 'aggregator.create-bank.steps.badge.complete'
                  : 'aggregator.create-bank.steps.badge.pending'
              }
              ns="aggregator"
            />
          }
          data-testid={`bank-upload-review-checklist-status-${item.id}`}
        />

        <Button
          type="button"
          variant="text"
          color="secondary"
          className={styles['review-link']}
          onClick={onReview}
          data-testid={`bank-upload-review-checklist-action-${item.id}`}
        >
          <Translation
            i18nKey="aggregator.create-bank.review.review-section"
            ns="aggregator"
          />
        </Button>
      </div>
    </li>
  ),
});
